/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import StoreFilter from "../components/store-filter";
import { useFilterState } from "@/pages/conciliation/stores/ducks/filter/hooks/filterState";
import { useBalanceState } from "../ducks/hooks/state";
import { useBalanceActions } from "../ducks/hooks/action";
import { NEW_RESPONSIBLE_CPF_MESSAGES } from "../data/messages";
import { useNewResponsibleCpf } from "../ducks/hooks/use-new-responsible-cpf";
import type { IRequestParams } from "@/@types/cash-balance";
import BalanceCard from "../components/balance-card";
import DownloadReportButton from "../components/download-button";
import { toast } from "components/ui/use-toast";
import { useLazyGetCashBalanceReportQuery } from "@/services/cash-balance";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Loader2 } from "lucide-react";

export const BalanceCashRequest: React.FC = () => {
  const { lastProcessedStore } = useBalanceState();
  const { updateLastProcessedStore, resetBalance } = useBalanceActions();

  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const { code, name: storeName } = filters || {};

  const storeCode = Array.isArray(code) ? code[0] : code;
  const storeId = Array.isArray(filters?.code)
    ? filters.code[0]
    : filters?.code;

  const [fetchBalance, { data, error, isFetching }] =
    useLazyGetCashBalanceReportQuery();
  const { maskedCpf, cpfDigits, cpfError, isCpfReady, handleCpfChange } =
    useNewResponsibleCpf();

  useEffect(() => {
    if (storeCode && lastProcessedStore && storeCode !== lastProcessedStore) {
      resetBalance();
    }
  }, [storeCode, lastProcessedStore, resetBalance]);

  if (error) {
    const errorMessage =
      (error as any)?.data?.message ||
      "Erro ao buscar o saldo. Tente novamente.";

    toast({
      description: errorMessage,
      variant: "destructive",
    });
  }

  const isExpired =
    !data ||
    (data.data.expire_at && new Date() > new Date(data.data.expire_at));

  const shouldShowBalance =
    !isFetching &&
    !isExpired &&
    data &&
    data.data.created_at &&
    lastProcessedStore === storeCode &&
    lastProcessedStore !== null;

  const handleRequestBalance = () => {
    if (!storeId || !isCpfReady) return;
    const params: IRequestParams = cpfDigits
      ? { storeId, newResponsibleCpf: cpfDigits }
      : { storeId };
    resetBalance();
    updateLastProcessedStore(storeCode || null);
    fetchBalance({ params });
  };

  return (
    <PageLayout>
      <PageTitle
        title="Saldo de Loja"
        description="Faça as solicitações do Saldo de Loja"
      />
      <ContentContainer>
        <div className="flex flex-col w-full max-w-3xl">
          <div className="w-full mt-5">
            <StoreFilter />
          </div>

          {storeCode && (
            <div className="w-full mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex flex-col gap-2 w-full sm:max-w-xs">
                <Label htmlFor="new-responsible-cpf">
                  {NEW_RESPONSIBLE_CPF_MESSAGES.LABEL}
                </Label>
                <Input
                  id="new-responsible-cpf"
                  inputMode="numeric"
                  placeholder={NEW_RESPONSIBLE_CPF_MESSAGES.PLACEHOLDER}
                  value={maskedCpf}
                  onChange={(event) => handleCpfChange(event.target.value)}
                  aria-invalid={Boolean(cpfError)}
                />
                {cpfError && (
                  <p className="text-sm text-destructive">{cpfError}</p>
                )}
              </div>
              <Button
                onClick={handleRequestBalance}
                isLoading={isFetching}
                className="text-lg py-3 px-6"
                disabled={!storeCode || isFetching || !isCpfReady}
              >
                Solicitar Saldo de Loja
              </Button>
            </div>
          )}

          {isFetching && lastProcessedStore && (
            <div className="w-full mt-8 flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-lg text-gray-600">
                Carregando saldo da loja {lastProcessedStore}...
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Por favor, aguarde enquanto buscamos as informações
              </p>
            </div>
          )}

          {shouldShowBalance && (
            <div className="w-full mt-6">
              <BalanceCard
                storeCode={lastProcessedStore || ""}
                storeName={
                  Array.isArray(storeName)
                    ? storeName[0]
                    : String(storeName || "")
                }
                previousBalance={data.data.previous_balance}
                entries={data.data.entries}
                exits={data.data.exits}
                dailyClosing={data.data.daily_closing}
                dailyBalance={data.data.daily_balance}
                createdAt={new Date(data.data.created_at)}
              />
              <div className="flex justify-end mt-4">
                <DownloadReportButton
                  storeCode={lastProcessedStore || ""}
                  previousBalance={data.data.previous_balance}
                  entries={data.data.entries}
                  exits={data.data.exits}
                  dailyClosing={data.data.daily_closing}
                  dailyBalance={data.data.daily_balance}
                  createdAt={new Date(data.data.created_at)}
                  treasuryName={data.data.treasury_name}
                  newResponsibleCpf={data.data.new_responsible_cpf}
                  buttonText={"Exportar Relatório"}
                />
              </div>
            </div>
          )}
        </div>
      </ContentContainer>
    </PageLayout>
  );
};
